/**
 * EulaModal — Conditions d'utilisation & avis de confidentialité (popup).
 *
 * Affiché à l'inscription. L'utilisateur doit cocher « J'ai lu et j'accepte »
 * pour pouvoir s'inscrire ; il peut ouvrir ce popup pour lire le texte intégral.
 *
 * NOTE : ce texte est un modèle de bonne foi et ne constitue pas un avis
 * juridique. Il est recommandé de le faire relire par un professionnel du droit
 * avant la mise en production "live".
 */

import { useEffect, useRef } from 'react';
import './EulaModal.css';

function EulaModal({ open, onClose }) {
  const closeBtnRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    // Empêcher le défilement de l'arrière-plan
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // Focus sur le bouton de fermeture pour l'accessibilité clavier
    closeBtnRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="eula-overlay"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="eula-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="eula-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="eula-header">
          <h2 id="eula-title" className="eula-title">
            Conditions d'utilisation et protection des données
          </h2>
          <button
            type="button"
            className="eula-close"
            onClick={onClose}
            aria-label="Fermer les conditions d'utilisation"
            ref={closeBtnRef}
          >
            ×
          </button>
        </div>

        <div className="eula-body">
          <p className="eula-intro">
            En créant un compte sur <strong>Grand‑Électeur</strong>, vous acceptez les
            conditions ci‑dessous. Veuillez les lire attentivement avant de poursuivre
            votre inscription.
          </p>

          <h3>1. Protection et sécurité de vos données</h3>
          <p>
            La protection de vos données personnelles est une priorité. Les informations
            que vous fournissez sont collectées dans le seul but de faciliter votre
            accompagnement à l'inscription électorale et le suivi de votre dossier.
            Elles sont stockées de manière sécurisée, l'accès en est strictement limité
            aux personnes autorisées, et elles ne sont <strong>jamais vendues</strong> à
            des tiers à des fins commerciales. Vous pouvez demander la consultation, la
            rectification ou la suppression de vos données à tout moment.
          </p>

          <h3>2. Données de localisation</h3>
          <p>
            Grand‑Électeur collecte des données de localisation dans la seule mesure
            nécessaire à ses finalités (Loi n° 2008‑12 du 25 janvier 2008 sur la
            protection des données à caractère personnel) : une position GPS précise
            lorsque vous lancez explicitement une recherche du relais le plus proche,
            et une position approximative rafraîchie silencieusement à la connexion
            (par GPS si vous l'avez déjà autorisé, sinon estimée à partir de votre
            adresse déclarée ou de votre connexion internet). Ces données servent
            uniquement à vous mettre en relation avec le relais le plus proche de vous
            et à la supervision du réseau : une carte des positions, réservée aux
            Administrateurs et aux Gouverneurs expressément habilités. Seule votre
            dernière position connue est conservée sur votre compte — chaque nouvelle
            capture remplace la précédente. Pour estimer une position approximative,
            votre adresse déclarée peut être transmise au service de géocodage
            OpenStreetMap (Nominatim) et votre adresse IP au service ipapi.co ; vos
            coordonnées elles‑mêmes ne sont jamais transmises à des tiers. Vos
            coordonnées ne sont <strong>jamais montrées aux autres citoyens</strong>.
          </p>

          <h3>3. Limitation de responsabilité</h3>
          <p>
            Grand‑Électeur met en œuvre des moyens raisonnables pour assurer la fiabilité,
            la disponibilité et la sécurité du service. Toutefois, la plateforme est
            fournie « en l'état », sans garantie d'absence totale d'erreur ou
            d'interruption. Dans les limites permises par la loi, Grand‑Électeur, ses
            partenaires et ses agents ne sauraient être tenus responsables des dommages
            directs ou indirects résultant de l'utilisation ou de l'impossibilité
            d'utiliser le service, d'une décision d'une administration tierce, ou d'un
            usage frauduleux ou inexact des informations par un utilisateur. Les démarches
            administratives officielles relèvent en dernier ressort des autorités
            compétentes.
          </p>

          <h3>4. Respect des autres utilisateurs</h3>
          <p>
            Grand‑Électeur réunit des citoyens, des relais de terrain, des parrains et des
            administrateurs. Vous vous engagez à faire preuve de courtoisie et de respect
            envers les autres utilisateurs. Tout propos haineux, harcèlement, discrimination,
            usurpation d'identité ou comportement nuisible est strictement interdit.
          </p>

          <h3>5. Droit de suspension et d'exclusion</h3>
          <p>
            Grand‑Électeur se réserve le droit, à sa seule discrétion et sans préavis, de
            suspendre, restreindre ou supprimer définitivement l'accès de tout utilisateur
            qui enfreindrait les présentes conditions, fournirait de fausses informations,
            compromettrait la sécurité de la plateforme ou nuirait aux autres utilisateurs.
          </p>

          <h3>6. Activités frauduleuses</h3>
          <p>
            Toute tentative de fraude, falsification de documents, usurpation d'identité ou
            manipulation des dispositifs de financement est formellement interdite. Les
            activités frauduleuses pourront faire l'objet d'un signalement aux
            <strong> autorités locales compétentes</strong> en vue d'une enquête et, le cas
            échéant, de poursuites et de sanctions prévues par la loi.
          </p>

          <h3>7. Acceptation</h3>
          <p>
            En cochant la case « J'ai lu et j'accepte les conditions d'utilisation », vous
            reconnaissez avoir pris connaissance de ces conditions et les accepter sans
            réserve. Ces conditions peuvent être mises à jour ; la version en vigueur est
            celle présentée lors de votre inscription ou de votre connexion.
          </p>
        </div>

        <div className="eula-footer">
          <button type="button" className="btn-primary" onClick={onClose}>
            J'ai compris
          </button>
        </div>
      </div>
    </div>
  );
}

export default EulaModal;
